import * as d3 from "d3";
import React from "react";

import { segmentArcAngles, segmentCount } from "../../chartGeometry";
import { ConfigData } from "../../config";
import { QuadrantConfig } from "../../model";

function arcPath(
  quadrantPosition: number,
  ringPosition: number,
  xScale: d3.ScaleLinear<number, number>,
  config: ConfigData
) {
  const [startAngle, endAngle] = segmentArcAngles(
    quadrantPosition,
    segmentCount(config)
  );
  const arcAttrs = config.chartConfig.ringsAttributes[ringPosition],
    ringRadiusPx = xScale(arcAttrs.radius) - xScale(0),
    arc = d3.arc();

  return (
    arc({
      innerRadius: ringRadiusPx - arcAttrs.arcWidth,
      outerRadius: ringRadiusPx,
      startAngle,
      endAngle,
    }) || undefined
  );
}

/**
 * Full pie sector of the segment, used as a clip path for the background
 * gradient.
 *
 * The radius is the half diagonal of the chart square, so the sector always
 * reaches the square's corners. For N = 4 the clipped area is therefore exactly
 * the quarter-square `<rect>` this replaces; for any other N a rectangle simply
 * cannot represent a sector.
 */
function sectorPath(
  quadrantPosition: number,
  config: ConfigData,
  halfSize: number
) {
  const [startAngle, endAngle] = segmentArcAngles(
    quadrantPosition,
    segmentCount(config)
  );

  return (
    d3.arc()({
      innerRadius: 0,
      outerRadius: halfSize * Math.SQRT2,
      startAngle,
      endAngle,
    }) || undefined
  );
}

const QuadrantRings: React.FC<{
  quadrant: QuadrantConfig;
  xScale: d3.ScaleLinear<number, number>;
  config: ConfigData;
}> = ({ quadrant, xScale, config }) => {
  const gradientId = `${quadrant.position}-radial-gradient`,
    clipId = `${quadrant.position}-sector-clip`,
    size = config.chartConfig.size,
    quadrantSize = size / 2;

  return (
    <g className="quadrant-ring">
      {/* Definition of the segment gradient and of the sector it is clipped to */}
      <defs>
        <radialGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          cx={quadrantSize}
          cy={quadrantSize}
          r={quadrantSize}
        >
          <stop offset="0%" stopColor={quadrant.colour}></stop>
          <stop
            offset="100%"
            stopColor={quadrant.colour}
            stopOpacity="0"
          ></stop>
        </radialGradient>
        <clipPath id={clipId}>
          <path
            d={sectorPath(quadrant.position, config, quadrantSize)}
            transform={`translate(${quadrantSize}, ${quadrantSize})`}
          />
        </clipPath>
      </defs>

      {/* Gradient background area, clipped to the segment's sector */}
      <rect
        width={size}
        height={size}
        x={0}
        y={0}
        fill={`url(#${gradientId})`}
        clipPath={`url(#${clipId})`}
        style={{ opacity: 0.5 }}
      />

      {/* Rings' arcs */}
      {Array.from(config.rings).map((ringPosition, index) => (
        <path
          key={index}
          fill={quadrant.colour}
          d={arcPath(quadrant.position, index, xScale, config)}
          style={{
            transform: `translate(${quadrantSize}px, ${quadrantSize}px)`,
          }}
        />
      ))}
    </g>
  );
};

export default QuadrantRings;
